import { getStringValueFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { ProgressOutput } from 'libs/end-to-end/src/progress';
import { DisparityField } from 'libs/end-to-end/src/progress';
import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';
import { Color } from 'libs/end-to-end/src/types/branding/color';
import { roundToPrecision } from 'libs/end-to-end/src/round-to-precision';
import { S2GroupedBarChartUnit } from 'libs/end-to-end/src/grouped-bar';
import { ProgressYearProps } from 'libs/end-to-end/src/progress';
import { formatNumberForDisplayInUnits } from 'libs/end-to-end/src/number-formatting';
import { ProgressRequest } from 'libs/end-to-end/src/custom-viz/ProgressTypes';
import { getMetricByNameFromObservation } from 'libs/end-to-end/src/me';
import { getMetricDisplayNameFromObservation } from 'libs/end-to-end/src/me';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { capitalize } from 'lodash';
import { getPrivilegedLevelForDimensionFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { determineUnitOfMetric } from 'libs/end-to-end/src/metrics';
import { CustomVizPollCompleteResponse } from 'libs/end-to-end/src/custom-viz/CustomVizDefinitions';
import { Phrases } from 'libs/end-to-end/src/phrases';
import { formatNumber } from 'libs/end-to-end/src/number-formatting';

export const getProgressDataExample = () => {
  return {
    pollStatus: 'complete',
    createdAt: Date.now(),
    outputContents:
      '{"focusArea": "Attrition", "metric": "attrition", "dimension": "education", "rateGroups": [{"name": "2023", "levels": [{"level": "Bachelor\'s degree", "value": 0.25, "count": 12}, {"level": "Associate\'s degree", "value": 0.222, "count": 18}, {"level": "Doctoral degree", "value": 0.35, "count": 20}, {"level": "Master\'s degree", "value": 0.2, "count": 15}, {"level": "No degree", "value": 0.17600000000000002, "count": 17}, {"level": "Professional degree", "value": -0.00053, "count": 17}]}, {"name": "2022", "levels": [{"level": "Bachelor\'s degree", "value": 0.75, "count": 12}, {"level": "Associate\'s degree", "value": 0.429, "count": 7}, {"level": "Doctoral degree", "value": 0.182, "count": 11}, {"level": "Master\'s degree", "value": 0.444, "count": 9}, {"level": "No degree", "value": 0.5, "count": 4}, {"level": "Professional degree", "value": 0.33299999999999996, "count": 12}]}, {"name": "2021", "levels": [{"level": "Bachelor\'s degree", "value": 0.5710000000000001, "count": 7}, {"level": "Associate\'s degree", "value": 0.667, "count": 6}, {"level": "Doctoral degree", "value": 0.33299999999999996, "count": 9}, {"level": "Master\'s degree", "value": 0.5, "count": 8}, {"level": "No degree", "value": 0.5710000000000001, "count": 7}, {"level": "Professional degree", "value": 0.364, "count": 11}]}], "rawDisparityGroups": [{"name": "2023", "levels": [{"level": "Associate\'s degree", "value": -0.111, "count": 18}, {"level": "Doctoral degree", "value": 0.4, "count": 20}, {"level": "Master\'s degree", "value": -0.2, "count": 15}, {"level": "No degree", "value": -0.294, "count": 17}, {"level": "Professional degree", "value": -0.059000000000000004, "count": 17}]}, {"name": "2022", "levels": [{"level": "Associate\'s degree", "value": -0.429, "count": 7}, {"level": "Doctoral degree", "value": -0.758, "count": 11}, {"level": "Master\'s degree", "value": -0.40700000000000003, "count": 9}, {"level": "No degree", "value": -0.33299999999999996, "count": 4}, {"level": "Professional degree", "value": -0.556, "count": 12}]}, {"name": "2021", "levels": [{"level": "Associate\'s degree", "value": 0.16699999999999998, "count": 6}, {"level": "Doctoral degree", "value": -0.41700000000000004, "count": 9}, {"level": "Master\'s degree", "value": -0.125, "count": 8}, {"level": "No degree", "value": 0.0, "count": 7}, {"level": "Professional degree", "value": -0.364, "count": 11}]}], "controlledDisparityGroups": [{"name": "2023", "levels": [{"level": "Associate\'s degree", "value": -0.33, "count": 18}, {"level": "Doctoral degree", "value": 0.419, "count": 20}, {"level": "Master\'s degree", "value": -0.10099999999999999, "count": 15}, {"level": "No degree", "value": -0.6940000000000001, "count": 17}, {"level": "Professional degree", "value": -0.289, "count": 17}]}, {"name": "2022", "levels": [{"level": "Associate\'s degree", "value": -0.574, "count": 7}, {"level": "Doctoral degree", "value": -0.5670000000000001, "count": 11}, {"level": "Master\'s degree", "value": -0.359, "count": 9}, {"level": "No degree", "value": -0.114, "count": 4}, {"level": "Professional degree", "value": -0.47100000000000003, "count": 12}]}, {"name": "2021", "levels": [{"level": "Associate\'s degree", "value": -0.006, "count": 6}, {"level": "Doctoral degree", "value": 0.048, "count": 9}, {"level": "Master\'s degree", "value": 0.027000000000000003, "count": 8}, {"level": "No degree", "value": -0.16, "count": 7}, {"level": "Professional degree", "value": -0.248, "count": 11}]}], "isMetricBoolean": true}',
  } satisfies CustomVizPollCompleteResponse;
};

export const getProgressNegativeDataExample = () => {
  return {
    pollStatus: 'complete',
    createdAt: Date.now(),
    outputContents:
      '{"focusArea": "Attrition", "metric": "attrition", "dimension": "gender", "rawDisparityGroups": [{"name": "2024", "levels": [{"level": "Male", "value": 198770.85928, "count": 222}, {"level": "Female", "value": 144858.72019999998, "count": 100}]}, {"name": "2023", "levels": [{"level": "Male", "value": 190868.6887, "count": 245}, {"level": "Female", "value": 140199.4713, "count": 117}]}, {"name": "2022", "levels": [{"level": "Male", "value": 185111.72353, "count": 255}, {"level": "Female", "value": 132911.93704, "count": 128}]}], "rateGroups": [{"name": "2024", "levels": [{"level": "Female", "value": -0.341, "count": 100}]}, {"name": "2023", "levels": [{"level": "Female", "value": -0.341, "count": 117}]}, {"name": "2022", "levels": [{"level": "Female", "value": -0.359, "count": 128}]}], "controlledDisparityGroups": [{"name": "2024", "levels": [{"level": "Female", "value": -0.027000000000000003, "count": 100}]}, {"name": "2023", "levels": [{"level": "Female", "value": -0.005, "count": 117}]}, {"name": "2022", "levels": [{"level": "Female", "value": -0.038, "count": 128}]}], "isMetricBoolean": true}',
  } satisfies CustomVizPollCompleteResponse;
};

export const getDimensionFilterValue = (
  primaryFilterValues: PrimaryFilterSelection[],
) => {
  return getStringValueFromPrimaryFilterValues({
    primaryFilterKey: 'dimension',
    primaryFilterValues,
  });
};

export const getComparisonGroupFilterValue = (
  primaryFilterValues: PrimaryFilterSelection[],
) => {
  return getStringValueFromPrimaryFilterValues({
    primaryFilterKey: 'comparisonGroup',
    primaryFilterValues,
  });
};

export const getPipelineAnalysisConfigurationFilterValue = (
  primaryFilterValues: PrimaryFilterSelection[],
) => {
  return getStringValueFromPrimaryFilterValues({
    primaryFilterKey: 'pipelineAnalysisConfiguration',
    primaryFilterValues,
  });
};

export const parseProgressData = ({
  data,
  disparity,
  getLevelByColumnValue,
}: {
  data: ProgressOutput;
  disparity: DisparityField;
  getLevelByColumnValue: (
    columnHeaderValue: string,
    columnValue: string,
  ) => LevelDefinition | undefined;
}) => {
  const rateGroups = data[disparity];

  // if metric is boolean we have to multiply the values by 100 and display
  // them as percentages
  const metricMultiplier =
    data.isMetricBoolean ||
    ['rawDisparityGroups', 'controlledDisparityGroups'].includes(disparity)
      ? 100
      : 1;

  const parsedData = rateGroups.map((rg) => ({
    year: rg.name,
    values: rg.levels.map(({ level, value, count, numerator }) => ({
      level,
      count: count ?? 0,
      value: value ? roundToPrecision(value * metricMultiplier, 1) : 0,
      color: getLevelColor(getLevelByColumnValue, data.dimension, level),
      isCheckered: false,
      numerator: data.isMetricBoolean ? numerator : null,
    })),
  }));

  return parsedData.sort((a, b) => a.year.localeCompare(b.year));
};

export const getLevelColor = (
  getLevelByColumnValue: (
    columnHeaderValue: string,
    columnValue: string,
  ) => LevelDefinition | undefined,
  dimension: string,
  level: string,
): Color => {
  const levelDefinition = getLevelByColumnValue(dimension, level);

  if (levelDefinition) {
    return levelDefinition.color;
  }

  return 'Gray';
};

/**
 * Utility function to round axis value to "nice" numbers like 1, 2,
 * 5, 10, 50, 100, etc.
 */
export const roundAxisValue = (value: number): number => {
  const exponent = Math.floor(Math.log10(value));
  const fraction = value / Math.pow(10, exponent);
  let niceFraction;

  if (fraction < 1.5) {
    niceFraction = 1;
  } else if (fraction < 3) {
    niceFraction = 2;
  } else if (fraction < 7) {
    niceFraction = 5;
  } else {
    niceFraction = 10;
  }

  return Math.ceil(niceFraction * Math.pow(10, exponent));
};

/**
 * Utility function to get the tooltip metrics for a given year and level.
 */
export const getTooltipMetrics = ({
  data,
  level,
  units,
  index,
  language,
  whiteLabelingSubjectPlural,
}: {
  data: ProgressYearProps[];
  level: string;
  units: S2GroupedBarChartUnit;
  index: number;
  language: Phrases;
  whiteLabelingSubjectPlural: string;
}) => {
  const yearData = data[index];
  const yearLevel = yearData.values.find((v) => v.level === level);

  const formatNumberFn = (value: number) => {
    return formatNumberForDisplayInUnits({
      value,
      units,
      // For some reason, progress pre-multiplies percentages by 100,
      isPercentageAlreadyMultipliedBy100: true,
    });
  };

  // create array with metrics for current year being hovered
  const tooltipMetrics = [
    {
      key: `${yearData.year}Value`,
      primaryNumber: `${formatNumberFn(yearLevel?.value ?? 0)}`,
      secondaryText: `in ${yearData.year}`,
    },
  ];

  // if the hovered year is not the first year, add a metric for the difference
  // between the current year and the previous year
  if (index > 0) {
    const previousYearData = data[index - 1];
    const previousYearLevel = previousYearData.values.find(
      (v) => v.level === level,
    );
    const difference =
      (yearLevel?.value ?? 0) - (previousYearLevel?.value ?? 0);
    const differenceString = formatNumberFn(difference);

    const formattedDifference =
      difference > 0 ? `+${differenceString}` : differenceString;

    tooltipMetrics.push({
      key: `${previousYearData.year}Value`,
      primaryNumber: `${formattedDifference}`,
      secondaryText: `since ${previousYearData.year}`,
    });
  }

  // add a metric for the number of people in the current year
  if (Number.isInteger(yearLevel?.numerator)) {
    tooltipMetrics.push({
      key: `${yearData.year}Count`,
      primaryNumber: '',
      secondaryText: language.subjectsCount({
        subjectsCount: String(yearLevel?.numerator ?? 0),
        totalSample: String(yearLevel?.count ?? 0),
        whiteLabelingSubjectPlural,
      }),
    });
  } else {
    tooltipMetrics.push({
      key: `${yearData.year}Count`,
      primaryNumber: `${formatNumber(yearLevel?.count ?? 0)}`,
      secondaryText: 'people',
    });
  }

  return tooltipMetrics;
};

/**
 * Utility function to add an alpha channel to a color.
 */
export const addAlpha = (color: string, opacity: number): string => {
  // coerce values so ti is between 0 and 1.
  const _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
  return color + _opacity.toString(16).toUpperCase();
};

export const getPrimaryFilterSelectionsFromRequest = ({
  request,
}: {
  request: Pick<ProgressRequest, 'inputs'>;
}): PrimaryFilterSelection[] => {
  const extras = request.inputs.extras;

  const { dimension, focusArea, metric } = extras;

  return [
    {
      primaryFilterKey: 'focusArea',
      value: [focusArea, metric],
    },
    {
      primaryFilterKey: 'dimension',
      value: dimension,
    },
  ];
};

export const getProgressPageMetric = ({
  data,
}: {
  data: ProgressOutput | null;
}) => {
  if (!data?.focusArea || !data?.metric) {
    return null;
  }

  return data.metric;
};

export const getProgressPageMetricDisplayName = ({
  data,
  myObservation,
}: {
  data: ProgressOutput | null;
  myObservation: MyObservation | null;
}) => {
  if (!data) {
    return '';
  }

  const metricName = getProgressPageMetric({
    data,
  });

  return capitalize(
    getMetricDisplayNameFromObservation({
      myObservation,
      focusArea: data.focusArea,
      metricName: metricName ?? '',
    }) ?? '',
  );
};

export const getProgressPageWhiteLabelingSubjectPlural = ({
  data,
  myObservation,
}: {
  data: ProgressOutput | null;
  myObservation: MyObservation | null;
}) => {
  const metricName = getProgressPageMetric({
    data,
  });

  if (!metricName || !data || !myObservation) {
    return 'employees';
  }

  const metricDefinition = getMetricByNameFromObservation({
    metricName,
    focusArea: data.focusArea,
    myObservation,
  });

  if (!metricDefinition) {
    return 'employees';
  }

  return metricDefinition.whiteLabeling?.subjectPlural ?? 'employees';
};

export const getProgressPageUnits = ({
  data,
  selectedDisparityIndex,
  myObservation,
}: {
  data: ProgressOutput | null;
  selectedDisparityIndex: number;
  myObservation: MyObservation | null;
}): S2GroupedBarChartUnit => {
  const focusArea = data?.focusArea;

  if (!focusArea) {
    return 'count';
  }

  if (selectedDisparityIndex > 0) {
    return 'percentage';
  }

  const metricDefinition = getMetricByNameFromObservation({
    metricName: data?.metric ?? '',
    focusArea,
    myObservation,
  });

  return determineUnitOfMetric({
    knownMaximum: null,
    isMetricBoolean: data?.isMetricBoolean ?? false,
    metricFocusArea: data?.focusArea ?? '',
    definitionUnit: metricDefinition?.unit,
  });
};

export const getComparisonGroupValueFromRequest = ({
  request,
  myObservation,
}: {
  request: ProgressRequest;
  myObservation: MyObservation | null;
}) => {
  // Currently, progress only supports the comparison group which is the privileged dimension.
  const dimension = request.inputs.extras.dimension;

  const privilegedLevel = getPrivilegedLevelForDimensionFromObservation(
    myObservation,
    dimension,
  );

  if (!privilegedLevel) {
    return null;
  }

  return privilegedLevel.columnValue;
};
